import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './Button';
import { Search } from './Search';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close menu on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);
  const navItems = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Programs',
    path: '/programs'
  }, {
    name: 'Impact',
    path: '/impact'
  }, {
    name: 'Stories',
    path: '/stories'
  }, {
    name: 'Structure',
    path: '/structure'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMenuOpen ? 'bg-white/95 backdrop-blur-md shadow-soft py-3' : 'bg-transparent py-5'}`} role="banner" data-id="element-139">
      <div className="container mx-auto flex justify-between items-center" data-id="element-140">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 z-50 group" onClick={closeMenu} aria-label="SFRR Foundation Home" data-id="element-141">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aXeXdP1aRi4Xt5RFum28MJ/Untitled_design.png" alt="SFRR Foundation Logo" className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105" loading="eager" width="56" height="56" data-id="element-142" />
          <div className={`hidden lg:block font-serif font-bold text-base leading-tight transition-colors duration-300 ${isScrolled || isMenuOpen ? 'text-charcoal' : 'text-white'}`} data-id="element-143">
            Senator Fatima
            <br data-id="element-144" />
            Raji Rasaki Foundation
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation" data-id="element-145">
          {navItems.map(item => <Link key={item.path} to={item.path} className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-full ${location.pathname === item.path ? 'text-primary bg-primary/10' : isScrolled ? 'text-charcoal-700 hover:text-primary hover:bg-neutral-100' : 'text-white/90 hover:text-white hover:bg-white/10'}`} aria-current={location.pathname === item.path ? 'page' : undefined} data-id="element-146">
              {item.name}
              {location.pathname === item.path && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full" aria-hidden="true" data-id="element-147" />}
            </Link>)}

          {/* Search */}
          <div className="ml-2" data-id="element-148">
            <Search data-id="element-149" />
          </div>

          {/* Donate Button */}
          <div className="ml-2" data-id="element-150">
            <Link to="/donate" data-id="element-151">
              <Button variant={isScrolled ? 'primary' : 'white'} size="sm" data-id="element-152">
                Donate Now
              </Button>
            </Link>
          </div>
        </nav>

        {/* Mobile: Search + Menu Button */}
        <div className="lg:hidden flex items-center gap-2 z-50" data-id="element-153">
          <Search data-id="element-154" />
          <button className={`p-2 rounded-full transition-all duration-300 ${isScrolled || isMenuOpen ? 'text-charcoal hover:bg-neutral-100' : 'text-white hover:bg-white/10'}`} onClick={toggleMenu} aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMenuOpen} aria-controls="mobile-menu" data-id="element-155">
            {isMenuOpen ? <X size={28} data-id="element-156" /> : <Menu size={28} data-id="element-157" />}
          </button>
        </div>

        {/* Mobile Navigation Overlay */}
        <div id="mobile-menu" className={`fixed inset-0 bg-white z-40 flex flex-col transition-all duration-500 lg:hidden ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`} role="dialog" aria-modal="true" aria-label="Mobile navigation" data-id="element-158">
          <div className="flex-1 flex flex-col items-center justify-center px-6" data-id="element-159">
            <nav className="flex flex-col items-center gap-2 text-center w-full max-w-sm" role="navigation" data-id="element-160">
              {navItems.map((item, index) => <Link key={item.path} to={item.path} className={`w-full text-2xl font-serif font-semibold py-4 px-6 rounded-2xl transition-all duration-300 ${location.pathname === item.path ? 'text-primary bg-primary/10' : 'text-charcoal hover:text-primary hover:bg-neutral-100'} ${isMenuOpen ? 'animate-fadeInUp' : ''}`} style={{
              animationDelay: `${index * 50}ms`
            }} onClick={closeMenu} aria-current={location.pathname === item.path ? 'page' : undefined} data-id="element-161">
                  {item.name}
                </Link>)}
              <Link to="/get-involved" onClick={closeMenu} className={`w-full text-2xl font-serif font-semibold py-4 px-6 rounded-2xl text-charcoal hover:text-primary hover:bg-neutral-100 transition-all duration-300 ${isMenuOpen ? 'animate-fadeInUp' : ''}`} style={{
              animationDelay: `${navItems.length * 50}ms`
            }} data-id="element-162">
                Get Involved
              </Link>
            </nav>

            <div className={`mt-8 ${isMenuOpen ? 'animate-fadeInUp' : ''}`} style={{
            animationDelay: `${(navItems.length + 1) * 50}ms`
          }} data-id="element-163">
              <Link to="/donate" onClick={closeMenu} data-id="element-164">
                <Button variant="primary" size="lg" data-id="element-165">
                  Donate Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>;
}